import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  signal,
  computed,
  ChangeDetectionStrategy,
  ElementRef,
  ViewChild,
  HostListener,
  inject,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewEncapsulation,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormsModule,
  ReactiveFormsModule,
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import {
  Overlay,
  OverlayRef,
  OverlayModule,
  ConnectedPosition,
} from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';

// ─── Modèle public ───────────────────────────────────────────────────────────

export interface TreeNodeAutoComplete {
  id?:       any;
  type?:     string;
  label:     string;
  children?: TreeNodeAutoComplete[];
  /** Affiche une pastille devant l'option — à l'appelant d'en définir le sens. */
  marked?:   boolean;
}

// ─── Nœud plat interne ───────────────────────────────────────────────────────

interface FlatOption {
  id:      any;
  type?:   string;
  label:   string;
  level:   number;
  isLeaf:  boolean;
  marked?: boolean;
  source:  TreeNodeAutoComplete;
}

// ─── Flatten ─────────────────────────────────────────────────────────────────

function flattenOptions(
  nodes: TreeNodeAutoComplete[],
  level = 0,
  result: FlatOption[] = [],
): FlatOption[] {
  for (const node of nodes) {
    result.push({
      id:     node.id,
      type:   node.type,
      label:  node.label,
      level,
      isLeaf: !node.children?.length,
      marked: node.marked,
      source: node,
    });
    if (node.children?.length) flattenOptions(node.children, level + 1, result);
  }
  return result;
}

// ─── Composant ───────────────────────────────────────────────────────────────

@Component({
  selector:      'app-autocomplete',
  standalone:    true,
  imports:       [CommonModule, FormsModule, ReactiveFormsModule, OverlayModule],
  templateUrl:   './autocomplete.component.html',
  styleUrls:     ['./autocomplete.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation:   ViewEncapsulation.None,
  providers: [{
    provide:     NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => AutocompleteComponent),
    multi:       true,
  }],
})
export class AutocompleteComponent implements ControlValueAccessor, OnChanges, OnDestroy {

  // ── Inputs / Outputs ──────────────────────────────────────────────────────
  @Input() data:        TreeNodeAutoComplete[] = [];
  @Input() placeholder = '';
  @Input() disabled    = false;

  /** Restaure la dernière valeur validée si le champ est vidé/modifié puis quitté (blur)
   *  sans sélection. Utile pour un sélecteur qui ne doit jamais rester vide. */
  @Input() revertOnBlur = false;

  /** Largeur imposée au panneau déroulant (défaut = largeur courante du champ). */
  @Input() dropdownWidth?: number | string;

  @Output() selected    = new EventEmitter<TreeNodeAutoComplete>();
  @Output() textChange  = new EventEmitter<string>();

  @ViewChild('inputWrapper') inputWrapperRef!: ElementRef<HTMLElement>;
  @ViewChild('dropdownTpl')  dropdownTpl!:     TemplateRef<any>;

  private readonly _overlay = inject(Overlay);
  private readonly _vcr     = inject(ViewContainerRef);
  private _overlayRef: OverlayRef | null = null;

  // ── State ─────────────────────────────────────────────────────────────────
  readonly inputText   = signal('');
  readonly isFocused   = signal(false);
  readonly isOpen      = signal(false);
  readonly activeIndex = signal(-1);

  readonly showScrollbar = signal(false);
  readonly thumbHeight   = signal(0);
  readonly thumbTop      = signal(0);

  // ── Données ───────────────────────────────────────────────────────────────
  private readonly _allOptions  = signal<FlatOption[]>([]);
  private readonly _searchText  = signal('');

  readonly filteredOptions = computed(() => {
    const all = this._allOptions();
    const q   = this._searchText().trim().toLowerCase();
    if (!q) return all;

    const visibleSources = new Set<TreeNodeAutoComplete>();

    for (const opt of all) {
      if (!opt.label.toLowerCase().includes(q)) continue;

      if (opt.isLeaf) {
        visibleSources.add(opt.source);
        this._getAncestors(opt.source, this.data).forEach(a => visibleSources.add(a));
      } else {
        visibleSources.add(opt.source);
        this._getLeafDescendants(opt.source).forEach(l => {
          visibleSources.add(l);
          this._getAncestors(l, this.data).forEach(a => visibleSources.add(a));
        });
      }
    }

    return all.filter(o => visibleSources.has(o.source));
  });

  private readonly _leaves = computed(() =>
    this.filteredOptions().filter(o => o.isLeaf)
  );

  readonly activeNode = computed(() => this._leaves()[this.activeIndex()] ?? null);

  // ── CVA ───────────────────────────────────────────────────────────────────
  private _onChange:  (v: any) => void = () => {};
  private _onTouched: () => void       = () => {};

  // Dernière valeur validée (sélection ou writeValue) — sert au revertOnBlur
  private _committedLabel = '';

  writeValue(val: TreeNodeAutoComplete | string | null): void {
    if (typeof val === 'string') this.inputText.set(val);
    else this.inputText.set(val?.label ?? '');
    this._committedLabel = this.inputText();
  }
  registerOnChange(fn: (v: any) => void): void  { this._onChange = fn; }
  registerOnTouched(fn: () => void): void        { this._onTouched = fn; }
  setDisabledState(isDisabled: boolean): void    { this.disabled = isDisabled; }

  // ── Lifecycle ─────────────────────────────────────────────────────────────
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      this._allOptions.set(flattenOptions(this.data));
    }
  }

  ngOnDestroy(): void {
    this._overlayRef?.dispose();
    document.removeEventListener('mousemove', this._onMouseMove);
    document.removeEventListener('mouseup',   this._onMouseUp);
  }

  // ── Interactions ──────────────────────────────────────────────────────────

  onNativeInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.inputText.set(value);
    this._searchText.set(value);
    this.activeIndex.set(-1);
    this.textChange.emit(value);
    this._onChange(value);
    this._openDropdown();
    requestAnimationFrame(() => this._initScrollbar());
  }

  onInputFocus(): void {
    this.isFocused.set(true);
    this._searchText.set(this.inputText());
  }

  onInputBlur(): void {
    this.isFocused.set(false);
    this._onTouched();

    // Restaure la dernière valeur validée si le champ a été vidé/modifié sans sélection.
    // Différé (setTimeout) pour laisser un éventuel clic sur une option s'exécuter
    // d'abord (selectOption met alors à jour _committedLabel → revert = no-op).
    if (this.revertOnBlur) {
      setTimeout(() => {
        if (this.inputText() !== this._committedLabel) {
          this.inputText.set(this._committedLabel);
          this._searchText.set(this._committedLabel);
        }
      });
    }
  }

  onInputClick(): void {
    if (this._overlayRef?.hasAttached()) return;
    this._searchText.set('');
    this._openDropdown();
    requestAnimationFrame(() => this._initScrollbar());
  }

  onOptionHover(opt: FlatOption): void {
    this.activeIndex.set(this._leaves().indexOf(opt));
  }

  selectOption(opt: FlatOption): void {
    if (!opt.isLeaf) return;
    this.inputText.set(opt.label);
    this._committedLabel = opt.label;
    this._closeDropdown();
    this._onChange(opt.source);
    this._onTouched();
    this.selected.emit(opt.source);
  }

  isActive(opt: FlatOption): boolean {
    return this.activeNode() === opt;
  }

  clearInput(event: MouseEvent): void {
    event.stopPropagation();
    this.inputText.set('');
    this._searchText.set('');
    this.activeIndex.set(-1);
    this.textChange.emit('');
    this._onChange('');
    this._onTouched();
    this._closeDropdown();
  }

  // ── Clavier ───────────────────────────────────────────────────────────────

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (!this.isOpen()) return;
    const max = this._leaves().length - 1;
    const idx = this.activeIndex();

    switch (event.key) {
      case 'ArrowDown': event.preventDefault(); this.activeIndex.set(Math.min(idx + 1, max)); break;
      case 'ArrowUp':   event.preventDefault(); this.activeIndex.set(Math.max(idx - 1, 0));   break;
      case 'Enter':     event.preventDefault(); if (this.activeNode()) this.selectOption(this.activeNode()!); break;
      case 'Escape':    this._closeDropdown(); break;
    }
  }

  // ── Scrollbar ─────────────────────────────────────────────────────────────

  private get _scrollEl(): HTMLElement | null {
    return this._overlayRef?.overlayElement?.querySelector('.autocomplete__scroll-content') ?? null;
  }

  private get _trackEl(): HTMLElement | null {
    return this._overlayRef?.overlayElement?.querySelector('.autocomplete__scrollbar-track') ?? null;
  }

  onContentScroll(): void {
    this._syncThumb();
  }

  private _initScrollbar(): void {
    const el = this._scrollEl;
    if (!el) return;
    const needs = el.scrollHeight > el.clientHeight;
    this.showScrollbar.set(needs);
    this._syncThumb();
  }

  private _syncThumb(): void {
    const content = this._scrollEl;
    const track   = this._trackEl;
    if (!content || !track) return;
    const h = Math.max(32, (content.clientHeight / content.scrollHeight) * track.clientHeight);
    this.thumbHeight.set(h);
    const maxScroll = content.scrollHeight - content.clientHeight;
    const ratio     = maxScroll > 0 ? content.scrollTop / maxScroll : 0;
    this.thumbTop.set(ratio * (track.clientHeight - h));
  }

  // ── Drag ──────────────────────────────────────────────────────────────────

  private _isDragging   = false;
  private _dragStartY   = 0;
  private _dragStartTop = 0;
  private _onMouseMove  = this._handleMouseMove.bind(this);
  private _onMouseUp    = this._handleMouseUp.bind(this);

  onTrackClick(event: MouseEvent): void {
    const track   = this._trackEl;
    const content = this._scrollEl;
    if (!track || !content) return;
    const clickY = event.clientY - track.getBoundingClientRect().top;
    content.scrollTop = (clickY / track.clientHeight) * (content.scrollHeight - content.clientHeight);
    this._syncThumb();
  }

  onThumbMouseDown(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this._isDragging   = true;
    this._dragStartY   = event.clientY;
    this._dragStartTop = this._scrollEl?.scrollTop ?? 0;
    document.addEventListener('mousemove', this._onMouseMove);
    document.addEventListener('mouseup',   this._onMouseUp);
  }

  private _handleMouseMove(e: MouseEvent): void {
    if (!this._isDragging) return;
    const content = this._scrollEl;
    const track   = this._trackEl;
    if (!content || !track) return;
    const ratio = (content.scrollHeight - content.clientHeight) / (track.clientHeight - this.thumbHeight());
    content.scrollTop = this._dragStartTop + (e.clientY - this._dragStartY) * ratio;
    this._syncThumb();
  }

  private _handleMouseUp(): void {
    this._isDragging = false;
    document.removeEventListener('mousemove', this._onMouseMove);
    document.removeEventListener('mouseup',   this._onMouseUp);
  }

  // ── Overlay ───────────────────────────────────────────────────────────────

  private _openDropdown(): void {
    if (this._overlayRef?.hasAttached()) return;

    this._overlayRef = this._overlay.create({
      positionStrategy: this._overlay
        .position()
        .flexibleConnectedTo(this.inputWrapperRef)
        .withPositions([
          { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top',    offsetY:  4 },
          { originX: 'start', originY: 'top',    overlayX: 'start', overlayY: 'bottom', offsetY: -4 },
        ] as ConnectedPosition[])
        .withPush(false),
      scrollStrategy: this._overlay.scrollStrategies.reposition(),
      width:          this.dropdownWidth ?? this.inputWrapperRef.nativeElement.getBoundingClientRect().width,
      hasBackdrop:    true,
      backdropClass:  'cdk-overlay-transparent-backdrop',
    });

    this._overlayRef.backdropClick().subscribe(() => {
      this._closeDropdown();
      this._onTouched();
    });

    this._overlayRef.attach(new TemplatePortal(this.dropdownTpl, this._vcr));
    this.isOpen.set(true);

    requestAnimationFrame(() => requestAnimationFrame(() => this._initScrollbar()));
  }

  private _closeDropdown(): void {
    this._overlayRef?.dispose();
    this._overlayRef = null;
    this.showScrollbar.set(false);
    this.isOpen.set(false);
  }

  // ── Tree helpers ──────────────────────────────────────────────────────────

  private _getLeafDescendants(node: TreeNodeAutoComplete): TreeNodeAutoComplete[] {
    if (!node.children?.length) return [node];
    return node.children.flatMap(c => this._getLeafDescendants(c));
  }

  private _getAncestors(
    target: TreeNodeAutoComplete,
    nodes:  TreeNodeAutoComplete[],
    path:   TreeNodeAutoComplete[] = [],
  ): TreeNodeAutoComplete[] {
    for (const node of nodes) {
      if (node === target) return path;
      if (node.children?.length) {
        const found = this._getAncestors(target, node.children, [...path, node]);
        if (found.length) return found;
      }
    }
    return [];
  }
}